import { observer } from "mobx-react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { startCase } from "lodash";
import {
    AGENDA_VOTING_PROCESS,
    AGENDA_VOTING_PROCESS_STEP
} from "src/stores/assemblies/AgendaVotingProgressModalStore";
import styles from "./AgendaVotingProgressModal.module.css";
import { AgendaVotingInformation } from "./AgendaVotingInformation";
import { AgendaVotingOptions } from "./AgendaVotingOptions";
import { runInAction } from "mobx";

const AgendaVotingProgressModalBase = () => {
    const { t } = useTranslation();
    const { uiStore, votingStore, agendaVotingProgressModalStore, agendaitemStore } = useContext(RootStoreContext);
    const { setCurrentProcessType, currentStepType, currentStep, VOTING_MODAL_MAX_NUMBER_OF_STEPS } =
        agendaVotingProgressModalStore;

    useEffect(() => {
        if (!votingStore.isNewVoting) {
            votingStore.loadVotingInformationBasis();
            votingStore.loadVotingDetails(votingStore.selectedVotingId, agendaitemStore.currentAgendaItem?.id || "");
            votingStore.loadVotingOptions();
        }
    }, [votingStore, votingStore.votingBasis.votingId, votingStore.isNewVoting, agendaitemStore.currentAgendaItem]);

    const onClickStep = useCallback(
        (processType: AGENDA_VOTING_PROCESS) => () => {
            setCurrentProcessType(processType);
            if (processType === AGENDA_VOTING_PROCESS.AGENDA_VOTING_OPTIONS) {
                votingStore.saveVotingInformationBasis();
            }
        },
        [setCurrentProcessType, votingStore]
    );

    const onCloseModal = useCallback(async () => {
        if (votingStore.isNewVoting) {
            if (votingStore.votingBasis.votingId.length > 0) {
                await votingStore.deleteVotingById(votingStore.votingBasis.votingId);
            }
        }
        votingStore.resetAllVotingData();
        votingStore.setIsVotingModalDisplayed(false);
        agendaVotingProgressModalStore.resetCurrentProcessType();
    }, [votingStore, agendaVotingProgressModalStore]);

    const onSaveAllVotingInformation = useCallback(async () => {
        await votingStore.saveVotingOptions();

        const agendaItemId = agendaitemStore.currentAgendaItem?.id;
        if (agendaItemId) {
            runInAction(() => {
                votingStore.lastLoadedAgendaitemId = null;
            });
            await votingStore.loadAllVotingsByAgendaItemId(agendaItemId);
        }

        votingStore.setIsVotingModalDisplayed(false);
        agendaVotingProgressModalStore.resetCurrentProcessType();
    }, [votingStore, agendaVotingProgressModalStore, agendaitemStore.currentAgendaItem]);

    const renderStep = () => {
        switch (currentStepType) {
            case AGENDA_VOTING_PROCESS_STEP.START_AGENDA_VOTING:
            default:
                return (
                    <>
                        <AgendaVotingInformation />
                        <UI.Row className={`mt-5 ${styles.ButtonRow}`}>
                            <UI.Col xs={12} md={3}>
                                <UI.Button
                                    className={`ticket-button mt-2 ${styles.DiscardButton}`}
                                    label={startCase(t("labels.cancel").toString())}
                                    variant={"outline-primary"}
                                    onClick={onCloseModal}
                                ></UI.Button>
                            </UI.Col>
                            <UI.Col xs={12} md={3}>
                                <UI.Button
                                    className={"ticket-button mt-2"}
                                    label={t("labels.continue").charAt(0).toUpperCase() + t("labels.continue").slice(1)}
                                    onClick={onClickStep(AGENDA_VOTING_PROCESS.AGENDA_VOTING_OPTIONS)}
                                    disabled={
                                        votingStore.votingBasis.title.length === 0 ||
                                        votingStore.votingBasis.question.length === 0
                                    }
                                ></UI.Button>
                            </UI.Col>
                        </UI.Row>
                    </>
                );
            case AGENDA_VOTING_PROCESS_STEP.AGENDA_VOTING_OPTIONS:
                return (
                    <>
                        <AgendaVotingOptions />
                        <UI.Row className={"mt-5"}>
                            <UI.Col xs={12} md={3}>
                                <UI.Button
                                    className={`ticket-button mt-2 ${styles.DiscardButton}`}
                                    label={startCase(t("labels.cancel").toString())}
                                    variant={"outline-primary"}
                                    onClick={onCloseModal}
                                />
                            </UI.Col>
                            <UI.Col className={styles.RightButtonGroup}>
                                <UI.Col xs={12} md={4}>
                                    <UI.Button
                                        className={`ticket-button mt-2 ${styles.DiscardButton}`}
                                        label={t("labels.goback").toString()}
                                        variant={"outline-primary"}
                                        onClick={onClickStep(AGENDA_VOTING_PROCESS.START_AGENDA_VOTING)}
                                    />
                                </UI.Col>
                                <UI.Col xs={12} md={4}>
                                    <UI.Button
                                        className="ticket-button mt-2"
                                        label={t("labels.save")}
                                        onClick={onSaveAllVotingInformation}
                                        disabled={votingStore.selectedRealestateIds.length === 0}
                                    />
                                </UI.Col>
                            </UI.Col>
                        </UI.Row>
                    </>
                );
        }
    };

    return (
        <UI.ProgressModal
            currentStep={currentStep?.stepNumber ?? 1}
            totalSteps={VOTING_MODAL_MAX_NUMBER_OF_STEPS}
            title={t("screens.assemblies.agenda.voting")}
            show={votingStore.isVotingModalDisplayed}
            onDismissModal={onCloseModal}
            centered={uiStore.isMobile ? true : false}
            size={uiStore.isMobile ? undefined : "lg"}
        >
            {renderStep()}
        </UI.ProgressModal>
    );
};

export const AgendaVotingProgressModal = Sentry.withProfiler(observer(AgendaVotingProgressModalBase));
